import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { T } from "../../ui/tokens";
import { fmt, fmtDate } from "../../lib/format";
import { useEditarConta } from "./useExtrato";
import type { BancoConta } from "./types";

interface Props {
  conta: BancoConta;
  empresa?: string;
  empresaNome?: string;
  onFechar: () => void;
  onGuardado?: () => void;
}

const campo: React.CSSProperties = {
  width: "100%", padding: "9px 11px", borderRadius: 8,
  border: `1px solid ${T.border}`, fontSize: 13, color: T.text,
  background: T.surface, outline: "none",
};

const rotulo: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: T.textLight,
  textTransform: "uppercase", letterSpacing: ".06em",
};

/**
 * Edição de uma conta bancária — banco e número, por `conta_id`.
 *
 * O contexto (empresa, movimentos, extratos, período, saldo) é mostrado só
 * para leitura: serve para a pessoa confirmar QUE conta está a editar antes
 * de lhe mudar a identidade. Mudar o número de uma conta com movimentos
 * ligados não é uma correção cosmética.
 *
 * O conflito 409 (outra conta da mesma empresa já tem este número) não é
 * tratado como erro genérico: é explicado, porque a saída certa nesse caso é
 * comparar e fundir as duas contas, não insistir na edição.
 */
export default function ModalEditarConta({ conta, empresa, empresaNome, onFechar, onGuardado }: Props) {
  const [banco, setBanco] = useState(conta.banco || "");
  const [numero, setNumero] = useState(conta.conta || "");
  const [conflito, setConflito] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const editar = useEditarConta(empresa);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === "Escape") onFechar(); };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [onFechar]);

  const semAlteracoes = banco.trim() === (conta.banco || "").trim()
    && numero.trim() === (conta.conta || "").trim();

  const guardar = () => {
    setConflito(null);
    setErro(null);
    editar.mutate(
      { contaId: conta.conta_id, banco: banco.trim(), conta: numero.trim() },
      {
        onSuccess: () => { onGuardado?.(); onFechar(); },
        onError: (e: unknown) => {
          const err = e as Error & { status?: number };
          if (err.status === 409) setConflito(err.message);
          else setErro(err.message || "Não foi possível guardar.");
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Editar conta bancária"
      onClick={onFechar}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 400,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surface, borderRadius: 14, width: "min(460px, 100%)",
          maxHeight: "90vh", overflowY: "auto", border: `1px solid ${T.border}`,
          boxShadow: "0 20px 60px rgba(16,24,40,.24)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "18px 20px 12px" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>Editar conta bancária</div>
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 3 }}>
              {empresaNome || empresa || "Empresa ativa"}
            </div>
          </div>
          <button
            onClick={onFechar}
            aria-label="Fechar"
            style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, padding: 4, lineHeight: 0 }}
          >
            <X size={17} />
          </button>
        </div>

        <div style={{ padding: "0 20px 4px", display: "flex", flexDirection: "column", gap: 13 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={rotulo}>Banco</span>
            <input
              value={banco}
              onChange={(e) => setBanco(e.target.value)}
              placeholder="ex: Caixa Geral de Depósitos"
              style={campo}
              autoFocus
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span style={rotulo}>Número da conta</span>
            <input
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="IBAN, NIB ou número da conta"
              style={{ ...campo, fontFamily: "ui-monospace, monospace" }}
            />
          </label>
        </div>

        <div style={{ margin: "14px 20px", padding: "11px 13px", borderRadius: 9, background: T.bg, border: `1px solid ${T.borderSoft}` }}>
          <div style={{ ...rotulo, marginBottom: 7 }}>Esta conta tem</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "7px 12px", fontSize: 12.5, color: T.textMid }}>
            <div>Movimentos</div>
            <div style={{ fontWeight: 700, color: T.text, textAlign: "right" }}>{conta.total_movimentos}</div>
            <div>Extratos</div>
            <div style={{ fontWeight: 700, color: T.text, textAlign: "right" }}>{conta.total_extratos}</div>
            <div>Período</div>
            <div style={{ fontWeight: 700, color: T.text, textAlign: "right" }}>
              {conta.periodo_inicio
                ? `${fmtDate(conta.periodo_inicio)} — ${fmtDate(conta.periodo_fim || conta.periodo_inicio)}`
                : "—"}
            </div>
            {conta.saldo != null && (
              <>
                <div>Saldo conhecido</div>
                <div style={{ fontWeight: 700, color: T.text, textAlign: "right" }}>{fmt(conta.saldo)}</div>
              </>
            )}
          </div>
        </div>

        {conflito && (
          <div style={{ margin: "0 20px 12px", padding: "10px 13px", borderRadius: 9, background: T.amberBg, border: `1px solid ${T.amberBorder}`, color: T.amber, fontSize: 12.5, lineHeight: 1.5 }}>
            {conflito}
          </div>
        )}
        {erro && (
          <div style={{ margin: "0 20px 12px", padding: "10px 13px", borderRadius: 9, background: T.redBg, border: `1px solid ${T.redBorder}`, color: T.red, fontSize: 12.5 }}>
            {erro}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, padding: "4px 20px 18px" }}>
          <button
            onClick={onFechar}
            style={{ padding: "9px 15px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
          >
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={editar.isPending || semAlteracoes || !numero.trim()}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "9px 17px", borderRadius: 8, border: "none",
              background: editar.isPending || semAlteracoes || !numero.trim() ? T.textLight : T.blue,
              color: "#fff", fontSize: 12.5, fontWeight: 700,
              cursor: editar.isPending || semAlteracoes || !numero.trim() ? "default" : "pointer",
            }}
          >
            {editar.isPending && <Loader2 size={13} style={{ animation: "spin-extrato 0.8s linear infinite" }} />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
